import { completeOnboardingStepSchema } from "../schema";

import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import type {
	GetOnboardingStatusQuery,
	OnboardingStep,
} from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { onboardingQueryKeys } from "../queries/query-keys";

const completeOnboardingStepQuery = graphql(/* GraphQL */ `
	mutation CompleteOnboardingStep($step: OnboardingStep!) {
		user {
			onboarding {
				completeStep(step: $step) {
					steps {
						key
						value {
							isCompleted
						}
					}
				}
			}
		}
	}
`);

type OnboardingOverview = NonNullable<
	GetOnboardingStatusQuery["users"]["onboarding"]["overview"]
>;

function setStepCompleted(
	overview: OnboardingOverview | undefined,
	step: OnboardingStep,
): OnboardingOverview | undefined {
	if (!overview?.steps) return overview;
	return {
		...overview,
		steps: overview.steps.map((s) =>
			s.step === step ? { ...s, isCompleted: true } : s,
		),
	};
}

/**
 * Complete an onboarding step.
 * Uses optimistic cache updates; rolls back on mutation error.
 *
 * @returns React Query mutation object
 */
export const useCompleteOnboardingStep = (onSuccess?: () => void) => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (step: OnboardingStep) => {
			const result = await client.mutate({
				mutation: completeOnboardingStepQuery,
				variables: {
					step,
				},
			});

			const data = result.data?.user.onboarding.completeStep;
			return data ? completeOnboardingStepSchema.parse(data) : null;
		},
		onMutate: async (step) => {
			await queryClient.cancelQueries({
				queryKey: onboardingQueryKeys.all,
			});

			const previous = queryClient.getQueriesData<OnboardingOverview>({
				queryKey: onboardingQueryKeys.all,
			});

			queryClient.setQueriesData<OnboardingOverview>(
				{ queryKey: onboardingQueryKeys.all },
				(old) => setStepCompleted(old, step),
			);

			return { previous };
		},
		onError: (error, _step, context) => {
			if (context?.previous) {
				for (const [queryKey, data] of context.previous) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			handleError(error, "Failed to complete onboarding step");
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: onboardingQueryKeys.all,
			});
		},
		onSuccess: () => {
			onSuccess?.();
		},
	});
};
